/**
 * Designer Mode Store (Zustand)
 * Manages the three-mode designer system: Design, Execution, Evaluation
 *
 * State includes:
 * - Current designer mode (design/execution/evaluation)
 * - Execution tracking (current execution ID, status, progress)
 * - Per-node execution states
 * - Execution errors
 * - Selected historical execution (for evaluation mode)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  UIDesignerMode,
  ExecutionState,
  ExecutionProgress,
  ExecutionStatusData,
  NodeExecutionState,
  ExecutionIdentifier,
  ExecutionHistoryItem,
} from '@flow-studio/types';
import { processThreadExecutionApiClient } from '@flow-studio/api';
// Defined locally to avoid a circular dependency with @flow-studio/flow-observer-core
// (which dynamically imports @flow-studio/store). Must be kept in sync with
// flow-observer-core/src/types/nodeInspector.ts.
export interface NodeInspectorData {
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  outputPortKey?: string;
  durationMs?: number;
  retryAttempt?: number;
  maxRetries?: number;
  startedAt?: string;
  completedAt?: string;
  status?: string;
}

// Local utility function to avoid circular dependency with flow-studio-core
function mapExecutionState(statusName: string): ExecutionState {
  const mapping: Record<string, ExecutionState> = {
    Idle: 'idle',
    Queued: 'queued',
    Running: 'running',
    Paused: 'paused',
    Waiting: 'waiting',
    Completed: 'completed',
    CompletedWithWarnings: 'completedWithWarnings',
    Failed: 'failed',
    Cancelled: 'cancelled',
    TimedOut: 'timedOut',
  };
  return mapping[statusName] || 'idle';
}

// ============================================================================
// INTERFACES
// ============================================================================

// Re-export from execution.types to consolidate type definitions
export type { NodeExecutionState };

interface DesignerModeState {
  // Current Mode
  mode: UIDesignerMode;

  // Execution Tracking
  // Store complete ExecutionID with both resID (GUID) and id (numeric)
  // Use resID for all operations (SignalR, polling, controls)
  currentExecutionId: ExecutionIdentifier | null;
  executionState: ExecutionState;
  executionStartTime: string | null;
  executionEndTime: string | null;
  executionProgress: ExecutionProgress;
  /** True once useExecutionSignalR has confirmed JoinExecution for currentExecutionId. The
   *  toolbar's execute handler awaits this (join-before-execute) so the backend can't finish
   *  broadcasting live events before the client is in the SignalR group — see TopToolbar.tsx. */
  executionGroupJoined: boolean;

  // Per-Node Execution State
  nodeExecutionStates: Map<string, NodeExecutionState>;

  // Error Info
  executionError: {
    message: string;
    stackTrace?: string;
    nodeId?: number;
  } | null;

  // Evaluation Mode
  selectedHistoryExecutionId: number | null;
  /** Aggregate record for the selected historical execution — processThreadExecutionApiClient.getById only returns
   *  this flat summary (status/duration/node counts), not per-node event data, so this is what's available to show. */
  selectedHistoricalExecutionSummary: ExecutionHistoryItem | null;
  /** True while the summary fetch is in flight; distinguishes "still loading" from "loaded nothing"/"failed" in the UI. */
  isLoadingHistoricalExecutionSummary: boolean;
  /** Set when the summary fetch fails or comes back empty — lets the UI show a real error instead of an infinite "Loading". */
  selectedHistoricalExecutionError: string | null;

  // Node Inspector Data
  nodeInspectorData: Map<string, NodeInspectorData>;
  selectedInspectorNodeKey: string | null;

  // Actions
  setMode: (mode: UIDesignerMode) => void;
  startExecution: (executionId: ExecutionIdentifier) => void;
  setExecutionGroupJoined: (joined: boolean) => void;
  setExecutionNumericId: (id: number) => void;
  updateExecutionStatus: (statusData: ExecutionStatusData) => void;
  updateNodeStatus: (nodeId: string, status: Partial<NodeExecutionState>) => void;
  completeExecution: (state: ExecutionState) => void;
  setExecutionResult: (result: any) => void;
  selectHistoryExecution: (executionId: number) => Promise<void>;
  clearSelectedHistoryExecution: () => void;
  clearExecutionState: () => void;
  resetToDesignMode: () => void;
  setNodeInspectorData: (nodeKey: string, data: NodeInspectorData) => void;
  clearNodeInspectorData: () => void;
  setSelectedInspectorNodeKey: (nodeKey: string | null) => void;
}

// Helper function mapExecutionState is now imported from executionUtils

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const useDesignerModeStore = create<DesignerModeState>()(
  devtools(
    (set, get) => ({
      // ========================================================================
      // INITIAL STATE
      // ========================================================================
      mode: 'design',
      currentExecutionId: null,
      executionGroupJoined: false,
      executionState: 'idle',
      executionStartTime: null,
      executionEndTime: null,
      executionProgress: {
        totalNodes: 0,
        completedNodes: 0,
        failedNodes: 0,
        skippedNodes: 0,
        percentage: 0,
      },
      nodeExecutionStates: new Map(),
      executionError: null,
      selectedHistoryExecutionId: null,
      selectedHistoricalExecutionSummary: null,
      isLoadingHistoricalExecutionSummary: false,
      selectedHistoricalExecutionError: null,
      nodeInspectorData: new Map(),
      selectedInspectorNodeKey: null,

      // ========================================================================
      // ACTIONS
      // ========================================================================

      /**
       * Switch to a different mode
       */
      setMode: (mode) => set({ mode }),

      /**
       * Start execution tracking
       * Called when execution starts
       * Stores complete ExecutionID with both resID (GUID) and id (numeric)
       */
      startExecution: (executionId) => {
        set({
          currentExecutionId: executionId,  // { resID: "guid", id: number }
          executionGroupJoined: false,
          executionState: 'running',
          executionStartTime: new Date().toISOString(),
          executionEndTime: null,
          executionError: null,
          nodeExecutionStates: new Map(),
          nodeInspectorData: new Map(),
          selectedInspectorNodeKey: null,
          executionProgress: {
            totalNodes: 0,
            completedNodes: 0,
            failedNodes: 0,
            skippedNodes: 0,
            percentage: 0,
          },
        });
      },

      /**
       * Marks the SignalR execution group as joined (or not). Set by useExecutionSignalR
       * right after JoinExecution succeeds; polled by the toolbar's execute handler so it
       * can delay firing the /execute call until the client is actually in the group.
       */
      setExecutionGroupJoined: (joined) => set({ executionGroupJoined: joined }),

      /**
       * Patches in the backend-assigned numeric ID once the /execute response arrives,
       * without resetting nodeExecutionStates/nodeInspectorData — those may already hold
       * live data by the time the response comes back (join-before-execute means the
       * backend can start broadcasting before this response resolves).
       */
      setExecutionNumericId: (id) => {
        const current = get().currentExecutionId;
        if (!current) return;
        set({ currentExecutionId: { ...current, ID: id } });
      },

      /**
       * Update execution status with backend data
       * Called regularly from polling hook
       */
      updateExecutionStatus: (statusData) => {
        set({
          executionState: mapExecutionState(statusData.statusName),
          executionProgress: statusData.progress,
          executionError: statusData.errorInfo
            ? {
                message: statusData.errorInfo.message,
                stackTrace: statusData.errorInfo.stackTrace,
                nodeId: statusData.errorInfo.nodeID,
              }
            : null,
        });
      },

      /**
       * Update execution status for a specific node
       * Called from polling or real-time updates
       */
      updateNodeStatus: (nodeId, status) => {
        const states = new Map(get().nodeExecutionStates);
        const current = states.get(nodeId) || { nodeId, status: 'idle' };
        states.set(nodeId, { ...current, ...status });
        set({ nodeExecutionStates: states });
      },

      /**
       * Mark execution as complete
       * Called when execution finishes
       */
      completeExecution: (state) => {
        // Trigger nodes (Manual Trigger, etc.) fire once and hand off — the backend
        // never sends a discrete completion event for them, so they'd otherwise be
        // stuck showing "Running" forever even after the rest of the workflow
        // finishes successfully. Resolve any node still running/waiting when the
        // execution ends cleanly; leave them alone on failure/cancellation, where
        // "stuck running" is meaningful information about where things stopped.
        const isCleanFinish = state === 'completed' || state === 'completedWithWarnings';
        const nodeExecutionStates = isCleanFinish
          ? new Map(
              Array.from(get().nodeExecutionStates.entries()).map(([nodeId, nodeState]) =>
                nodeState.status === 'running' || nodeState.status === 'waiting' || nodeState.status === 'retrying'
                  ? [nodeId, { ...nodeState, status: 'success' as const, completedAt: nodeState.completedAt ?? new Date().toISOString() }]
                  : [nodeId, nodeState]
              )
            )
          : get().nodeExecutionStates;

        set({
          executionState: state,
          executionEndTime: new Date().toISOString(),
          nodeExecutionStates,
        });
      },

      /**
       * Set execution result from API response
       * Called when execution completes immediately with result data
       * Updates progress, timing, and error info from response
       *
       * Handles both backend response structures:
       * - New: { executionData: { ... }, state: { currentExecutionState: ... } }
       * - Legacy: { processExecutionData: { ... } }
       */
      setExecutionResult: (result: any) => {
        // Support multiple response structures (camelCase and PascalCase)
        const executionData =
          result.executionData ||
          result.ExecutionData ||  // PascalCase from backend
          result.processExecutionData ||
          result.ProcessExecutionData;
        const stateInfo = result.state || result.State;  // Support both cases

        if (!executionData) {
          console.warn('setExecutionResult: No execution data found in result', result);
          return;
        }

        // Map execution state number to state string
        // Backend eExecutionState enum: Idle=0, Queued=1, Running=2, Paused=3, Waiting=4,
        //                               Completed=5, CompletedWithWarnings=6, Failed=7, Cancelled=8, TimedOut=9
        const stateNumber =
          stateInfo?.currentExecutionState ||
          stateInfo?.CurrentExecutionState ||  // PascalCase
          executionData?.executionStatusID ||
          executionData?.ExecutionStatusID;  // PascalCase
        let executionState: ExecutionState = 'completed';

        if (stateNumber === 0) executionState = 'idle';
        else if (stateNumber === 1) executionState = 'queued';
        else if (stateNumber === 2) executionState = 'running';
        else if (stateNumber === 3) executionState = 'paused';
        else if (stateNumber === 4) executionState = 'waiting';
        else if (stateNumber === 5) executionState = 'completed';
        else if (stateNumber === 6) executionState = 'completedWithWarnings';
        else if (stateNumber === 7) executionState = 'failed';
        else if (stateNumber === 8) executionState = 'cancelled';
        else if (stateNumber === 9) executionState = 'timedOut';

        // Support both camelCase and PascalCase property names
        const startedAt = executionData?.startedAt || executionData?.StartedAt;
        const stoppedAt = executionData?.stoppedAt || executionData?.StoppedAt;
        const totalNodes = executionData?.totalNodes || executionData?.TotalNodes || 0;
        const completedNodes = executionData?.completedNodes || executionData?.CompletedNodes || 0;
        const failedNodes = executionData?.failedNodes || executionData?.FailedNodes || 0;
        const skippedNodes = executionData?.skippedNodes || executionData?.SkippedNodes || 0;
        const errorMessage = executionData?.errorMessage || executionData?.ErrorMessage;
        const errorStack = executionData?.errorStack || executionData?.ErrorStack;
        const errorNodeID = executionData?.errorNodeID || executionData?.ErrorNodeID;

        set({
          executionState,
          executionStartTime: startedAt || new Date().toISOString(),
          executionEndTime: stoppedAt || new Date().toISOString(),
          executionProgress: {
            totalNodes,
            completedNodes,
            failedNodes,
            skippedNodes,
            percentage: totalNodes ? Math.round((completedNodes / totalNodes) * 100) : 0,
          },
          executionError: errorMessage
            ? {
                message: errorMessage,
                stackTrace: errorStack,
                nodeId: errorNodeID,
              }
            : null,
        });
      },

      /**
       * Select a historical execution for evaluation. Switches to evaluation mode
       * immediately, then fetches the execution's summary record so the Executions
       * browser (Nodes Hierarchy tab) has something to display — previously this
       * only flipped the mode flag with no visible effect. `executionId` here is a
       * ProcessThreadExecutionID (the PK of the ProcessThreadExecution row), not a
       * ProcessExecutionID.
       */
      selectHistoryExecution: async (executionId) => {
        set({
          selectedHistoryExecutionId: executionId,
          mode: 'evaluation',
          selectedHistoricalExecutionSummary: null,
          selectedHistoricalExecutionError: null,
          isLoadingHistoricalExecutionSummary: true,
        });
        try {
          const response = await processThreadExecutionApiClient.getById(executionId);
          if (response.success && response.data) {
            set({ selectedHistoricalExecutionSummary: response.data, isLoadingHistoricalExecutionSummary: false });
          } else {
            set({
              selectedHistoricalExecutionError: response.error?.message || `Execution ${executionId} was not found`,
              isLoadingHistoricalExecutionSummary: false,
            });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          console.error('[DesignerModeStore] Failed to load historical execution', error);
          set({ selectedHistoricalExecutionError: message, isLoadingHistoricalExecutionSummary: false });
        }
      },

      /**
       * Dismiss the selected historical execution summary. Leaves `mode` untouched — see the
       * inline comment below for why forcing it back to 'design' here was actually a bug.
       */
      clearSelectedHistoryExecution: () => {
        // Deliberately does NOT touch `mode`. Every Observer Panel tab is registered with
        // showInModes: ['execution', 'evaluation'] — forcing mode back to 'design' here hides
        // every tab at once, which the panel store's own mode-subscription logic then reads as
        // "all tabs hidden" and auto-collapses the whole panel. That's what closing this one
        // summary sub-view was doing before: taking the entire Observer Panel down with it,
        // including a still-running live execution's Execution Status/Logs tabs.
        set({
          selectedHistoryExecutionId: null,
          selectedHistoricalExecutionSummary: null,
          selectedHistoricalExecutionError: null,
          isLoadingHistoricalExecutionSummary: false,
        });
      },

      /**
       * Clear all execution-related state
       * Called when leaving execution mode
       */
      clearExecutionState: () => {
        set({
          currentExecutionId: null,
          executionGroupJoined: false,
          executionState: 'idle',
          executionStartTime: null,
          executionEndTime: null,
          executionProgress: {
            totalNodes: 0,
            completedNodes: 0,
            failedNodes: 0,
            skippedNodes: 0,
            percentage: 0,
          },
          nodeExecutionStates: new Map(),
          executionError: null,
          nodeInspectorData: new Map(),
          selectedInspectorNodeKey: null,
        });
      },

      /**
       * Reset everything to design mode
       * Called when cancelling execution or going back to design
       */
      resetToDesignMode: () => {
        get().clearExecutionState();
        set({
          mode: 'design',
          selectedHistoryExecutionId: null,
        });
      },

      /**
       * Store inspector data for a specific node
       */
      setNodeInspectorData: (nodeKey, data) => {
        const current = new Map(get().nodeInspectorData);
        current.set(nodeKey, data);
        set({ nodeInspectorData: current });
      },

      /**
       * Clear all node inspector data
       */
      clearNodeInspectorData: () => {
        set({ nodeInspectorData: new Map() });
      },

      /**
       * Set the selected node key for the inspector tab
       */
      setSelectedInspectorNodeKey: (nodeKey) => {
        set({ selectedInspectorNodeKey: nodeKey });
      },
    }),
    { name: 'DesignerModeStore' }
  )
);

// ============================================================================
// EXPORTS
// ============================================================================

export type { DesignerModeState };
// NodeInspectorData is already exported above as an interface (not just re-exported)
